import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function RoleSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      isCenter: false,
    },
  });

  const [rolesData, setRolesData] = useState([]);

  const getRolesList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get("Role", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRolesData(response);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    getRolesList();
  }, [triggerRerender]);

  const handleEdit = async (row) => {
    try {
      const roleData = await get(`Role/${row.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      reset({
        ...roleData,
        id: roleData.id,
      });
    } catch (error) {
      console.error("Error editing role:", error);
    }
  };

  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this role?"
    );

    if (confirmDelete) {
      try {
        const formData = {
          id: rowId,
        };
        const response = await post(`Role/DeleteById`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.isSuccess) {
          toast.success(response.message || "Role deleted successfully!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting role:", error);
      }
    }
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      width: "150px",
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
      width: "250px",
    },
    {
      name: "Action",
      width: "80px",
      selector: (row) => (
        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn p-0"
            onClick={() => handleEdit(row)}
          >
            <i className="fas fa-pencil"></i>
          </button>
          {/* <button
            type="button"
            className="btn p-0"
            onClick={() => handleDelete(row.id)}
          >
            <i className="fas fa-trash"></i>
          </button> */}
        </div>
      ),
    },
  ];

  const exportToExcel = () => {
    const filteredData = rolesData.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      Description: row.description,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Roles");
    XLSX.writeFile(workbook, "Roles.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "ALL Roles";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const headers = ["Sr No", "Name", "Description"];

    const bodyData = rolesData.map((row, index) => {
      return [
        (currentPage - 1) * rowsPerPage + index + 1,
        row.name,
        row.description,
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("RolesList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post("Role", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          name: "",
          description: "",
        });
        getRolesList();
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error saving role:", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Roles</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSubmit(saveData)}>
                <div className="col-md-12">
                  <label className="form-label">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("name", {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    {...register("description", {
                      maxLength: {
                        value: 250,
                        message: "Maximum of 250 characters allowed.",
                      },
                    })}
                    className="form-control"
                    placeholder=""
                    rows="3"
                    onChange={handleChangeOfInputWithLengthCheck("description")}
                  />
                  {errors.description && (
                    <small className="text-danger">
                      {errors.description.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label">Is Center Role</label>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    {...register(`isCenter`)}
                  />
                  {errors.isCenter && (
                    <small className="text-danger">
                      {errors.isCenter.message}
                    </small>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div
                className="d-flex justify-content-end"
                style={{ marginTop: 0 }}
              >
                <div className="d-flex align-items-start gap-1">
                  <button
                    type="button"
                    data-bs-toggle="tooltip"
                    data-bs-custom-class="custom-tooltip"
                    title="Export to Excel"
                    className="btn btn-outline-success px-2 py-1"
                    onClick={exportToExcel}
                  >
                    <i className="fs-8 fa fa-file-excel"></i>
                  </button>
                  <button
                    type="button"
                    data-bs-toggle="tooltip"
                    data-bs-custom-class="custom-tooltip"
                    title="Export to PDF"
                    className="btn btn-outline-danger px-2 py-1"
                    onClick={exportToPDF}
                  >
                    <i className="fs-8 fa fa-file-pdf"></i>
                  </button>
                </div>
              </div>
              <div className="table-container fixed-last-column-table">
                <DataTable
                  columns={columns}
                  data={rolesData}
                  pagination
                  paginationPerPage={rowsPerPage}
                  paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                  dense
                  customStyles={customTableStyles}
                  highlightOnHover={true}
                  onChangePage={setCurrentPage}
                  onChangeRowsPerPage={setRowsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default RoleSetup;
